import {AddressWithContactModel} from '../models/checkout/Cart/AddressWithContact.model';
import {AddressModel} from '../models/checkout/Cart/Address.model';
import {StreetAddressModel} from '../models/checkout/Cart/StreetAddress.model';
import {ContactModel} from '../models/checkout/Cart/Contact.model';
import {
  getCountryByKey as getCountryByKeyFromAdapter,
  getCountryByShortKey as getCountryByShortKeyFromAdapter,
} from '@wix/wixstores-locale-dataset-adapter';
import {getCountryByKey, getCountryByShortKey} from '@wix/locale-dataset-javascript';
import {getCountryFromLocaleDataset, getFullSubdivisionCode} from './localeDataset.util';
import {getVatType, isCountryWithVAT} from './vat.util';
import {Address as MemberDetailsInfo, Street as MemberStreet} from '@wix/ambassador-addresses-api-v1-address/types';
import {AddressesFieldMasks} from '../../components/Checkout/constants';
import {isManualPaymentSelected, isPayPalSelected, mapStateFromISO_3166To2Letters} from './cashier.utils';
import {CartModel} from '../models/checkout/Cart/Cart.model';
import {mapNullToUndefined} from './mapperHelper.utils';
import {VatIdModel} from '../models/checkout/Cart/VatId.model';
import {CashierMandatoryField, CashierMandatoryFieldsOverrides} from '../../types/payment.types';
import {MFieldValuePairs} from '@wix/cashier-payments-widget/src/types/MandatoryFields';

export const mapMemberAddressToAddressWithContactModel = (
  memberAddress: MemberDetailsInfo,
  useLocalDatasetDirectlyInCheckout: boolean
): AddressWithContactModel => {
  const getCountryByKeyFn = useLocalDatasetDirectlyInCheckout ? getCountryByKey : getCountryByKeyFromAdapter;

  return new AddressWithContactModel({
    addressId: mapNullToUndefined(memberAddress.id),
    address: new AddressModel({
      streetAddress: isStreet(memberAddress.street)
        ? new StreetAddressModel({
            name: memberAddress.street?.name,
            number: memberAddress.street?.number,
          })
        : undefined,
      addressLine: !isStreet(memberAddress.street) ? mapNullToUndefined(memberAddress.addressLine1) : undefined,
      addressLine2: mapNullToUndefined(memberAddress.addressLine2),
      city: mapNullToUndefined(memberAddress.city),
      postalCode: mapNullToUndefined(memberAddress.zipCode),
      country: memberAddress?.country ? getCountryByKeyFn(memberAddress.country)?.shortKey : undefined,
      subdivision:
        memberAddress.subdivision && memberAddress.country
          ? getFullSubdivisionCode(memberAddress.country, memberAddress.subdivision, useLocalDatasetDirectlyInCheckout)
          : undefined,
    }),
    contact: new ContactModel({
      firstName: mapNullToUndefined(memberAddress.fullName!.firstName),
      lastName: memberAddress.fullName!.lastName,
      company: mapNullToUndefined(memberAddress.company),
      phone: mapNullToUndefined(memberAddress.phoneNumber),
      vatId: memberAddress.taxInfo?.id
        ? new VatIdModel({id: memberAddress.taxInfo.id, type: getVatType(memberAddress.taxInfo.type)})
        : undefined,
    }),
  });
};

const isStreet = (street?: MemberStreet): boolean => {
  return !!street?.name || !!street?.number;
};

export const mapContactModelToContactDetails = (contact?: ContactModel) => {
  return contact
    ? {
        firstName: contact.firstName ?? /* istanbul ignore next */ '',
        lastName: contact.lastName ?? /* istanbul ignore next */ '',
        ...(contact.phone ? {phone: contact.phone} : /* istanbul ignore next */ {}),
        ...(contact.company ? {company: contact.company} : /* istanbul ignore next */ {}),
        ...(contact.vatId ? /* istanbul ignore next */ {vatId: contact.vatId} : {}),
      }
    : /* istanbul ignore next */ {};
};

export const mapAddressModelToApiAddress = (address?: AddressModel) => {
  const streetAddress = address?.streetAddress
    ? {
        name: address.streetAddress.name ?? /* istanbul ignore next: test forms */ undefined,
        number: address.streetAddress.number ?? /* istanbul ignore next: test forms */ undefined,
      }
    : undefined;

  return address
    ? {
        country: address.country,
        city: address.city,
        ...(address.subdivision ? {subdivision: address.subdivision} : {}),
        ...(address.postalCode ? {postalCode: address.postalCode} : /* istanbul ignore next: test forms */ {}),
        ...(address.addressLine ? {addressLine: address.addressLine} : {}),
        ...(streetAddress ? {streetAddress} : {}),
        ...(address.addressLine2 ? {addressLine2: address.addressLine2} : {}),
      }
    : /* istanbul ignore next */ {};
};

/* eslint-disable sonarjs/cognitive-complexity */
export const mapToUpdateMemberAddressRequest = (
  contactDetails: ContactModel,
  address: AddressModel,
  useLocalDatasetDirectlyInCheckout: boolean,
  addressesServiceId?: string
): MemberDetailsInfo => {
  const getCountryByShortKeyFn = useLocalDatasetDirectlyInCheckout
    ? getCountryByShortKey
    : getCountryByShortKeyFromAdapter;

  return {
    id: addressesServiceId ?? null,
    fullName: {firstName: contactDetails.firstName, lastName: contactDetails.lastName},
    phoneNumber: contactDetails.phone ?? null,
    company: contactDetails.company ?? null,
    addressLine1:
      address.streetAddress?.name && address.streetAddress?.number
        ? `${address.streetAddress.name} ${address.streetAddress.number}`
        : address?.addressLine ?? /* istanbul ignore next */ null,
    addressLine2: address?.addressLine2 ?? null,
    street: {
      name: (address.streetAddress?.name ?? null) as unknown as string,
      number: (address.streetAddress?.number ?? null) as unknown as string,
      apt: null,
    },
    country: address?.country ? getCountryByShortKeyFn(address.country).key : /* istanbul ignore next */ null,
    zipCode: address?.postalCode ?? /* istanbul ignore next */ null,
    city: address?.city ?? /* istanbul ignore next */ null,
    subdivision: address?.subdivision
      ? address.subdivision.substring(address.subdivision.indexOf('-') + 1)
      : /* istanbul ignore next */ null,
    taxInfo: {
      id: (contactDetails.vatId?.id ?? null) as unknown as string,
      type: (contactDetails.vatId?.type ?? null) as unknown as string,
    },
  } as MemberDetailsInfo;
};

export const isBillingEqualToShipping = (
  billingAddressAndContact: AddressWithContactModel,
  shippingAddressAndContact: AddressWithContactModel
): boolean => {
  const shippingNoGeo: AddressWithContactModel | undefined = {
    ...shippingAddressAndContact,
    address: {
      ...shippingAddressAndContact.address,
      geocode: undefined,
    },
  };

  const billingToCompare = {
    address: billingAddressAndContact?.address,
    contact: billingAddressAndContact?.contact,
    addressId: billingAddressAndContact?.addressId,
  };
  const shippingToCompare = {
    address: shippingNoGeo?.address,
    contact: shippingNoGeo?.contact,
    addressId: shippingNoGeo?.addressId,
  };

  return JSON.stringify(shippingToCompare) === JSON.stringify(billingToCompare);
};

export const getFieldMasksFromAddress = (address: MemberDetailsInfo): AddressesFieldMasks[] => {
  const flatAddress = {
    [AddressesFieldMasks.firstName]: address.fullName?.firstName,
    [AddressesFieldMasks.lastName]: address.fullName?.lastName,
    [AddressesFieldMasks.country]: address.country,
    [AddressesFieldMasks.subdivision]: address.subdivision,
    [AddressesFieldMasks.city]: address.city,
    [AddressesFieldMasks.zipCode]: address.zipCode,
    [AddressesFieldMasks.phoneNumber]: address.phoneNumber,
    [AddressesFieldMasks.company]: address.company,
    [AddressesFieldMasks.addressLine1]: address.addressLine1,
    [AddressesFieldMasks.addressLine2]: address.addressLine2,
    [AddressesFieldMasks.streetName]: address.street?.name,
    [AddressesFieldMasks.streetNumber]: address.street?.number,
    [AddressesFieldMasks.taxId]: address.taxInfo?.id,
    [AddressesFieldMasks.taxType]: address.taxInfo?.type,
  };
  const fieldMasks = Object.keys(flatAddress).filter(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    (mask: string) => !!(flatAddress as any)[mask]
  ) as AddressesFieldMasks[];
  return fieldMasks.concat(AddressesFieldMasks.setAsDefault);
};

export function isAddressModelEmpty(model?: AddressModel) {
  return isModelEmpty(model);
}

export function isContactModelEmpty(model?: ContactModel) {
  return isModelEmpty(model);
}

function isModelEmpty(model?: object) {
  if (!model) {
    return true;
  }

  return Object.values(model).every((v) => v === undefined);
}

export const shouldHideBillingDetailsSection = (cart: CartModel, activePaymentId?: string): boolean => {
  const isBillingCountryExists = !!cart.paymentInfo.address?.country;
  const isShippingCountryExists = !!cart.deliveryInfo.address?.country;

  const isCountryAbsent = !isBillingCountryExists && !isShippingCountryExists;
  const isManualOrPayPalSelected = isManualPaymentSelected(activePaymentId) || isPayPalSelected(activePaymentId);
  const isNonShippableFlow = !cart.hasShippableItems;

  if (isCountryAbsent || isNonShippableFlow) {
    return false;
  }
  return !isCountryWithVAT(cart) && isManualOrPayPalSelected;
};

export const getBillingDetails = (params: {
  cart: CartModel;
  billingAddress?: AddressModel;
  billingContact?: ContactModel;
  activePaymentId?: string;
  setBillingSameAsShipping: boolean;
  selectedAddressesService?: AddressWithContactModel;
  selectedAddressesServiceId?: string;
}): {
  billingAddress?: AddressModel;
  billingContact?: ContactModel;
  billingAddressId?: string;
} => {
  const {
    cart,
    billingAddress,
    billingContact,
    activePaymentId,
    setBillingSameAsShipping,
    selectedAddressesService,
    selectedAddressesServiceId,
  } = params;

  if (setBillingSameAsShipping || shouldHideBillingDetailsSection(cart, activePaymentId)) {
    return {
      billingAddress: mapAddressModelToApiAddress(cart.deliveryInfo.address),
      billingContact: mapContactModelToContactDetails(cart.deliveryInfo.contact),
      billingAddressId: cart.deliveryInfo.addressId,
    };
  }

  if (selectedAddressesServiceId && selectedAddressesService) {
    return {
      billingAddress: mapAddressModelToApiAddress(selectedAddressesService.address),
      billingContact: mapContactModelToContactDetails(selectedAddressesService.contact),
      billingAddressId: selectedAddressesServiceId,
    };
  }

  return {
    billingAddress,
    billingContact,
    billingAddressId: selectedAddressesServiceId,
  };
};

export const getMandatoryFields = (
  address?: AddressModel,
  contactDetails?: ContactModel,
  email?: string
): MFieldValuePairs => {
  return {
    firstName: contactDetails?.firstName ?? '',
    lastName: contactDetails?.lastName ?? '',
    email: email ?? '',
    phone: contactDetails?.phone ?? '',
    countryCode: address?.country ? getCountryFromLocaleDataset(address?.country)?.key : '',
    state: address?.subdivision ? mapStateFromISO_3166To2Letters(address.subdivision) : '',
    city: address?.city ?? '',
    zipCode: address?.postalCode ?? '',
    address:
      address?.addressLine ??
      (address?.streetAddress?.name ? `${address?.streetAddress?.name} ${address?.streetAddress?.number}` : ''),
    holderId: contactDetails?.vatId?.id ?? '',
    street: address?.streetAddress?.name ?? '',
    houseNumber: address?.streetAddress?.number ?? '',
  };
};

export const isBasicMandatoryFieldsValid = (
  mandatoryFields: MFieldValuePairs,
  cashierMandatoryFields: CashierMandatoryFieldsOverrides
): boolean => {
  const isPhoneMandatory = Boolean(cashierMandatoryFields?.[CashierMandatoryField.phone]);

  return Boolean(
    mandatoryFields?.firstName && mandatoryFields?.lastName && (!isPhoneMandatory || mandatoryFields.phone)
  );
};
